import {Component, inject} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {MatIcon} from "@angular/material/icon";
import {MatMiniFabButton} from "@angular/material/button";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {FormGroup} from "@angular/forms";
import {BaseMarketingBlockPathStateService} from "./services/base-marketing-block-path-state.service";
import {BaseMarketingBlockModalService} from "./services/base-marketing-block-modal.service";
import {
  BaseMarketingBlockDialogData,
  MarketingBlockData,
  MarketingBlockFormField
} from "./types/base-marketing-block-types";
import {titleModal} from "./configs/base-marketing-block-modal-config";
import {defaultMarketingBlockData} from "./configs/default-marketing-block-data";
import {marketingBlockFormFields} from "./configs/marketing-block-form-fields";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList} from "@angular/cdk/drag-drop";
import {MbEditorComponent} from "./components/mb-editor/mb-editor.component";
import {MbFormComponent} from "./components/mb-form/mb-form.component";
import {RecordUpdateRequestParams} from "../../../../../../core/types/records-table.types";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-base-marketing-block-modal',
  imports: [
    BaseModalComponent,
    MatIcon,
    MatMiniFabButton,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    MbEditorComponent,
    MbFormComponent,
    LoaderComponent
  ],
  templateUrl: './base-marketing-block-modal.component.html',
  styleUrl: './base-marketing-block-modal.component.scss',
  providers: [ EditModeService ]
})
export class BaseMarketingBlockModalComponent extends BaseModalComponent {
  public baseMarketingBlockDialogData = inject<BaseMarketingBlockDialogData>(MAT_DIALOG_DATA);
  private baseMarketingBlockModalService: BaseMarketingBlockModalService = inject(BaseMarketingBlockModalService);
  private baseMarketingBlockPathStateService: BaseMarketingBlockPathStateService = inject(BaseMarketingBlockPathStateService);

  public marketingBlocksData: Array<MarketingBlockData> = [];
  public marketingBlockFormGroups: Array<FormGroup> = [];
  public noFirstData: boolean = false;
  public isValid: boolean = false;
  public isActiveEditor: boolean = false;
  public selectIndex: number = 0;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initComponent();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.baseMarketingBlockDialogData.modalKey);
    this.baseMarketingBlockPathStateService.resetPathState();
    this.baseMarketingBlockPathStateService.resetPathFileState();
  }

  public getTitleModal(): string {
    return titleModal[this.baseMarketingBlockDialogData.modalKey];
  }

  public handleAddNew() {
    const formGroup = this.baseModalService.getFormGroup<MarketingBlockFormField>(marketingBlockFormFields);
    this.marketingBlockFormGroups.push(formGroup);
    this.marketingBlocksData.push(defaultMarketingBlockData);
    this.isValid = false;
  }

  public updateMarketingBlockData(marketingBlockData: MarketingBlockData, index: number): void {
    this.marketingBlocksData[index] = marketingBlockData;
    this.marketingBlockFormGroups.at(index)?.patchValue(marketingBlockData);
    this.isValid = !!marketingBlockData.id;
    this.noFirstData = !!this.marketingBlocksData.length;
  }

  public deleteMarketingBlockData(index: number): void {
    this.marketingBlockFormGroups.splice(index, 1);
    this.marketingBlocksData.splice(index, 1);
    this.isValid = !!this.marketingBlocksData.length;
    this.noFirstData = !!this.marketingBlocksData.length;
    !this.isValid && this.handleAddNew();
  }

  public activeEditor(index: number): void {
    this.isActiveEditor = true;
    this.selectIndex = index;
  }

  public backClick(): void {
    this.isActiveEditor = false;
  }

  public isActiveDragAndDrop(): boolean {
    const lastItem = this.marketingBlocksData.at(-1);

    return !this.isEditModeActive &&
      this.marketingBlocksData.length > 1 &&
      !!lastItem?.id &&
      lastItem.id > 0 &&
      this.baseMarketingBlockDialogData.modalKey === 'usp';
  }

  public async handleDrop(event: CdkDragDrop<Array<MarketingBlockData>>): Promise<void> {
    const { previousIndex, currentIndex } = event;
    if (previousIndex === currentIndex) { return; }

    const movedItemId = this.marketingBlocksData.at(previousIndex)?.id;
    if (!movedItemId) { return; }

    this.loadingService.setIsLoadingState(true);
    const params: RecordUpdateRequestParams = {
      path: this.baseMarketingBlockPathStateService.pathState(),
      recordId: movedItemId,
      fieldName: 'priority',
      value: currentIndex + 1
    };

    await this.baseMarketingBlockModalService.updatedRecordField<MarketingBlockData>(params);
    await this.fetchDesignBlocksData();
  }

  private async initComponent(): Promise<void> {
    this.modalStatesService.openModal(this.baseMarketingBlockDialogData.modalKey);
    this.loadingService.setIsLoadingState(true);
    this.baseMarketingBlockPathStateService.setPathState(this.baseMarketingBlockDialogData.modalKey);
    this.baseMarketingBlockDialogData.modalKey === 'websiteBackups' &&
      this.baseMarketingBlockPathStateService.setPathFileState(this.baseMarketingBlockDialogData.modalKey);
    await this.fetchDesignBlocksData();
    this.isValid = !!this.marketingBlocksData.at(0)?.id;
    this.noFirstData = !!this.marketingBlocksData.at(0)?.id;
  }

  private async fetchDesignBlocksData(): Promise<void> {
    const params = this.baseMarketingBlockModalService.setDataInModalParams(this.baseMarketingBlockDialogData.parentId);
    this.marketingBlocksData = await this.baseMarketingBlockModalService.getDataInModal<MarketingBlockData>(params)
      .catch(() => [])
      .finally(() => this.loadingService.setIsLoadingState(false));
    if (this.baseMarketingBlockDialogData.modalKey === 'usp') {
      this.marketingBlocksData.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    }
    this.initForm();
  }

  private initForm(): void {
    if (!this.marketingBlocksData.length) {
      this.handleAddNew();
      return;
    }

    this.marketingBlockFormGroups = this.marketingBlocksData.map(data => {
      const formGroup = this.baseModalService.getFormGroup<MarketingBlockFormField>(marketingBlockFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });
  }
}
